import { createContext, useContext, useEffect, useState } from "react";
import EntityConstants from "../configs/EntityConstants";
import { GET, GET_PUBLIC } from "../service/CRUDService";
import { isPublicVerificationPortalEnabled } from "../configs/featureFlags";
import { appConfig } from "../configs/tenantConfig";
import i18next from "../i18n/i18n";

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const [portalAssets, setPortalAssets] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("portalAssets") || "null");
    } catch {
      return null;
    }
  });
  const [isLoadingPortalAssets, setIsLoadingPortalAssets] = useState(() => {
    try {
      return !JSON.parse(localStorage.getItem("portalAssets") || "null");
    } catch {
      return true;
    }
  });
  const [themeColors, setThemeColors] = useState({
    primaryColor: "#3538CD",
    primaryDark: "#3034a4",
    primaryLight: "#585cd9",
    primaryCardHover: "#7074e9",
    buttonColorPrimary: "#3538CD",
    buttonColorPrimaryHover: "#3538cddc",
    textHoverColor: "#ffffff",
    favicon: "",
  });

  const [verificationMethods, setVerificationMethods] = useState({
    with_qr: [],
    without_qr: [],
  });

  const [availableLanguages, setAvailableLanguages] = useState(() => {
    const stored = JSON.parse(
      localStorage.getItem("availableLanguages") || "null",
    );
    return (
      stored || [
        {
          value: appConfig.defaultLanguage,
          label: appConfig.defaultLanguage === "pt" ? "Português" : "English",
        },
      ]
    );
  });

  const updateFavicon = (faviconURL) => {
    if (!faviconURL) return;
    const link = document.querySelector("link[rel~='icon']");
    if (link) {
      link.href = faviconURL;
    } else {
      const newLink = document.createElement("link");
      newLink.rel = "icon";
      newLink.href = faviconURL;
      document.head.appendChild(newLink);
    }
  };

  const applyBrandTokens = (colors) => {
    const root = document.documentElement;
    root.style.setProperty("--brand-500", colors.primaryColor);
    root.style.setProperty("--brand-700", colors.primaryDark);
    root.style.setProperty(
      "--brand-200",
      `color-mix(in srgb, ${colors.primaryColor} 28%, transparent)`,
    );
    root.style.setProperty("--cf-button-color", colors.primaryColor);
    root.style.setProperty("--cf-primary-color", colors.primaryColor);
    root.style.setProperty("--cf-primary-dark-color", colors.primaryDark);
    root.style.setProperty("--cf-primary-light-color", colors.primaryLight);
    root.style.setProperty(
      "--cf-primary-card-hover-color",
      colors.primaryCardHover,
    );
    root.style.setProperty(
      "--button-color-primary",
      colors.buttonColorPrimary,
    );
    root.style.setProperty(
      "--button-color-primary-hover",
      colors.buttonColorPrimaryHover,
    );
    root.style.setProperty("--cf-text-hover-color", colors.textHoverColor);
  };

  const fetchThemeColors = async () => {
    try {
      const fetcher = isPublicVerificationPortalEnabled() ? GET_PUBLIC : GET;
      const response = await fetcher(
        EntityConstants.GET_ORGANISATION_DETAILS,
      );
      const data = await response.json();

      if (response.ok && data) {
        setPortalAssets(data);
        localStorage.setItem("portalAssets", JSON.stringify(data));

        const updatedColors = {
          primaryColor: data.primary_colour ?? "#3538CD",
          primaryDark: data.secondary_colour ?? "#3034a4",
          primaryLight: data.primary_colour ?? "#585cd9",
          primaryCardHover: data.secondary_colour ?? "#7074e9",
          buttonColorPrimary: data.primary_colour ?? "#3538CD",
          buttonColorPrimaryHover: data.secondary_colour ?? "#3538cddc",
          textHoverColor: data.secondary_colour ?? "#ffffff",
          favicon: data?.favicon ?? "",
        };

        setThemeColors(updatedColors);
        localStorage.setItem("themeColors", JSON.stringify(updatedColors));
        applyBrandTokens(updatedColors);
        updateFavicon(updatedColors.favicon);

        const verification = data.verification_methods || {};
        setVerificationMethods({
          with_qr: verification.with_qr || [],
          without_qr: verification.without_qr || [],
        });
        localStorage.setItem(
          "verificationMethods",
          JSON.stringify(verification || {}),
        );

        if (data.languages && Object.keys(data.languages).length > 0) {
          const langs = Object.entries(data.languages).map(([code, label]) => ({
            value: code,
            label: label,
          }));
          setAvailableLanguages(langs);
          localStorage.setItem("availableLanguages", JSON.stringify(langs));

          const userPref = localStorage.getItem("selectedLanguage");
          if (!userPref || !langs.some((l) => l.value === userPref)) {
            const defaultLang =
              langs.find((l) => l.value === appConfig.defaultLanguage)?.value ||
              langs[0].value;
            i18next.changeLanguage(defaultLang);
            localStorage.setItem("selectedLanguage", defaultLang);
            document.documentElement.lang = defaultLang;
          }
        }
      }
    } catch (error) {
      console.error("Error fetching theme colors:", error);
    } finally {
      setIsLoadingPortalAssets(false);
    }
  };

  useEffect(() => {
    const storedColors = JSON.parse(localStorage.getItem("themeColors"));
    const storedVerification = JSON.parse(
      localStorage.getItem("verificationMethods") || "null",
    );

    if (storedColors) {
      setThemeColors(storedColors);
      applyBrandTokens(storedColors);
      updateFavicon(storedColors.favicon);
    }

    if (storedVerification) {
      setVerificationMethods({
        with_qr: storedVerification.with_qr || [],
        without_qr: storedVerification.without_qr || [],
      });
    }

    fetchThemeColors();
  }, []);

  return (
    <ThemeContext.Provider
      value={{
        themeColors,
        portalAssets,
        isLoadingPortalAssets,
        fetchThemeColors,
        verificationMethods,
        availableLanguages,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
